import { useAction, useAtom } from '@reatom/npm-react'
import clsx from 'clsx'
import React from 'react'
import { NavLink } from 'react-router'
import styled from 'styled-components'

import { Tooltip, type IconProps as UiKitIconProps } from '@blade/ui-kit'
import { redirectToAuthEvent } from '@root/entities/auth'

import { navigationExpandedAtom } from '../atoms'

import type { ChildrenProps, ClassNameProps } from '@root/shared/react'
import type { IconProps } from '@root/shared/ui-kit'

type NavigationItemProps = {
  Icon: React.FC<IconProps> | React.FC<UiKitIconProps>
  to?: string
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  active?: boolean
  authRequired?: boolean
  tooltip?: string
} & ClassNameProps &
  ChildrenProps

export const NavigationItem: React.FC<NavigationItemProps> = ({
  className,
  Icon,
  children,
  to,
  onClick,
  active,
  authRequired = false,
  tooltip,
}) => {
  const [expanded] = useAtom(navigationExpandedAtom)
  const redirectToAuth = useAction(redirectToAuthEvent)

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (authRequired) {
      redirectToAuth()
    } else {
      onClick?.(e)
    }
  }

  const renderContent = () => (
    <>
      <Icon size={22} />
      <Label aria-expanded={expanded}>{children}</Label>
    </>
  )

  if (to && !authRequired) {
    return (
      <Tooltip side="right" content={tooltip}>
        <Root
          as={NavLink}
          onClick={onClick}
          to={to}
          className={className}
          aria-expanded={expanded}
        >
          {renderContent()}
        </Root>
      </Tooltip>
    )
  }

  return (
    <Tooltip side="right" content={tooltip}>
      <Root
        type="button"
        onClick={handleClick}
        className={clsx(className, active && 'active')}
        aria-expanded={expanded}
      >
        {renderContent()}
      </Root>
    </Tooltip>
  )
}

const Label = styled.div`
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  padding-inline-start: 16px;

  transition: opacity 0.1s ease-in-out;
  opacity: 0;

  &[aria-expanded='true'] {
    opacity: 1;
  }
`

const Root = styled.button`
  all: unset;
  box-sizing: border-box;
  display: grid;

  justify-content: start;
  justify-items: center;
  align-items: center;
  justify-items: start;
  height: 44px;
  width: 100%;

  transition: color 0.15s linear;
  cursor: pointer;
  text-decoration: none;
  color: var(--text-9);

  &:hover {
    color: var(--text-11);
  }
  &.active {
    color: var(--primary-9);
    background: var(--backgroundA-2);
    & ${Label} {
      color: var(--text-12);
    }
  }
  grid-template-columns: 20px 0;
  padding-inline-start: 16.5px;

  &[aria-expanded='true'] {
    grid-template-columns: 20px 1fr;
  }

  &:hover {
    background: var(--backgroundA-2);
  }

  @container sidebar (height <= 700px) {
    height: 40px;
  }
`
